import React from 'react';

import { CommentCard, MenuItem } from '@change/ds-core-ui';
import { ReportIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useOpenReportAbuseModal } from '#src/app/shared/components/abuse';

import type { SupporterComment, SupporterCommentPetition } from '../../shared/types';

import { useCommentLikes } from './hooks/useCommentLikes';
import { useReportComment } from './hooks/useReportComment';

export const SupporterCommentCard: React.FC<SupporterCommentCardProps> = ({
	supporterComment,
	petition,
	className,
}) => {
	const { translate } = useI18n();

	const {
		data: { liked, a11yLabel: likeButtonAriaLabel, likesCount, likes },
		actions: { toggleLiked },
	} = useCommentLikes({ comment: supporterComment });

	const {
		data: { canReportComment, a11yLabel: reportAriaLabel },
		actions: { handleReportClick },
	} = useReportComment({ petitionId: petition.id, comment: supporterComment });

	const reportLabel = translate('fe.components.comments.report');

	const { open: openReportAbuseModal, reportAbuseModals } = useOpenReportAbuseModal({
		reportContext: {
			type: 'supporter_comment',
			comment: {
				id: supporterComment.id,
				likes: supporterComment.likeCount,
				// PetitionSupporterComment has no `role` field (roles are pre-filtered to
				// SUPPORTER_COMMENT by the connection itself) — the tracked `role` property on the
				// resulting `supporter_comment_report_abuse` event is always this value, so it's
				// hardcoded here instead of threaded through from a fetched field.
				role: 'SUPPORTER_COMMENT',
			},
			petitionId: petition.id,
			petitionSlug: petition.slug,
		},
	});

	const handleReportAction = () => {
		handleReportClick();
		openReportAbuseModal();
	};

	const moreActions: React.ComponentProps<typeof CommentCard>['moreActions'] = [
		<MenuItem
			key="report-comment-menu-item"
			className="[&_svg]:h-4 [&_svg]:w-4"
			onAction={handleReportAction}
			leadingIcon={<ReportIcon />}
			label={reportLabel}
			aria-label={reportAriaLabel}
			data-testid={`supporter-comment-${supporterComment.id}-report-button`}
		/>,
	];

	const moreActionsAriaLabel = translate('corgi.components.petition_video.actions.aria_label.more_actions');
	const moreActionProps: LocalWithActions | LocalWithoutActions = canReportComment
		? {
				moreActions,
				moreActionsAriaLabel,
			}
		: {};
	return (
		<div className={className} data-testid={`supporter-comment-${supporterComment.id}`}>
			<CommentCard
				commentContent={supporterComment.text}
				maxCommentLines={3}
				userLocation={supporterComment.user.city ?? undefined}
				userName={supporterComment.user.shortDisplayName}
				liked={liked}
				likeCounter={likesCount === 0 ? undefined : likes}
				likeButtonAriaLabel={likeButtonAriaLabel}
				likeButtonDataQa="comment-toggle-like"
				onLikeChanged={toggleLiked}
				fullCommentLinkLabel={translate('corgi.petition_gamma.details.description_show_full_text')}
				{...moreActionProps}
			/>
			{reportAbuseModals}
		</div>
	);
};

type SupporterCommentCardProps = {
	supporterComment: SupporterComment;
	petition: SupporterCommentPetition;
	className?: string;
};

type LocalWithActions = {
	moreActions: NonNullable<React.ComponentProps<typeof CommentCard>['moreActions']>;
	moreActionsAriaLabel: string;
};

/**
 * Mirrors ds-core-ui's own `WithoutMoreActionsProps` shape (optional `never`-typed keys, not a
 * bare empty type) — needed so `{...moreActionProps}` below type-checks against CommentCard's
 * `moreActions`/`moreActionsAriaLabel` union without widening it to `| undefined`.
 */
type LocalWithoutActions = {
	moreActions?: never;
	moreActionsAriaLabel?: never;
};
